/**
 * BaselineSnapshotBody — renders the expanded detail for `baseline`
 * and `snapshot` versions.
 *
 * Backend audit (Commit 8 audit step):
 *   - Both ops created by version_service.take_snapshot
 *     (lines 385-436):
 *       diff = null
 *       agent_name = null
 *       array_key = null
 *       id_field = null
 *       record_ids = []
 *       cumulative_change_count = null  (see line 436)
 *   - The full agent_progress snapshot IS stored on the doc but is
 *     stripped from the list response and only available via the
 *     detail endpoint with include_snapshot=true. Not rendered here.
 *
 *   Mock-only fields NOT shipped (and omitted from this body):
 *     - agent_counts — per-agent record-count grid. Genuinely a
 *       follow-up backend item.
 *
 * Render: a short informational callout — there's no diff to show
 * for these marker entries; the card header already carries the
 * operation tone + summary + actor + timestamp.
 */
import React from 'react';
import { VersionRecord } from '../../state/types';
import { OPERATION_META } from '../../utils/operationMeta';
import Glyph from '../Glyph';
import styles from './operationBodies.module.css';
import bodyStyles from './BaselineSnapshotBody.module.css';

interface BaselineSnapshotBodyProps {
  version: VersionRecord;
}

export default function BaselineSnapshotBody({
  version,
}: BaselineSnapshotBodyProps) {
  if (
    version.operation_type !== 'baseline' &&
    version.operation_type !== 'snapshot'
  ) {
    return null;
  }

  const meta = OPERATION_META[version.operation_type];
  const isBaseline = version.operation_type === 'baseline';

  return (
    <div className={styles.body}>
      <div className={bodyStyles.callout}>
        <span className={bodyStyles.calloutIcon}>
          <Glyph name={meta.glyph} />
        </span>
        <div className={bodyStyles.calloutBody}>
          <div className={bodyStyles.calloutLabel}>{meta.label}</div>
          <div className={bodyStyles.calloutText}>
            {isBaseline
              ? 'First completion of the workflow. Marks the start of the version history — every later edit is recorded as a diff from this point forward.'
              : 'Periodic state checkpoint. Restoring to any version uses the most recent snapshot as the rebuild starting point, so snapshots bound the cost of a restore.'}
          </div>
          <div className={bodyStyles.calloutText}>
            This entry is{' '}
            <span className={bodyStyles.versionRef}>v{version.version_number}</span>{' '}
            in the timeline.
          </div>
        </div>
      </div>
    </div>
  );
}
